import { ChangeDetectionStrategy, Component, Inject, Injector, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { BehaviorSubject, Subject, combineLatest, distinctUntilChanged, filter, map, of, take, takeUntil, tap } from 'rxjs';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';
import { EventService } from '@go-modules/services/event/event.service';
import type { GoEvent } from '@go-modules/services/event/event.service';
import { ROUTE_NAMES, RouterService } from '@go-modules/services/router/router.service';
import { SessionListDataSource } from '@go-modules/services/session-list-datasource/session-list.datasource';
import { NgxSessionService } from '@go-modules/services/session/session.service';
import { sessionToken, Session as SessionModel } from 'go-modules/models/session/session.service';
import { activityToken } from 'go-modules/models/activity/activity.factory';
import type { ActivityFactory } from 'go-modules/models/activity/activity.factory';
import { TranscriptCacheService } from '@go-modules/services/transcript-cache/transcript-cache.service';
import { ProductTranslationParams } from '@go-modules/interfaces';
import { ActiveBrandService } from '@go-modules/services/active-brand';

@Component({
	selector: 'ngx-session-view',
	templateUrl: './session-view.component.html',
	styleUrls: ['./session-view.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class NgxSessionViewComponent implements OnInit, OnDestroy {
	public options$$: BehaviorSubject<any> = new BehaviorSubject({});
	public eventSubscription;
	public otherSessions;
	public isFullyLoaded$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
	public componentDestroyed$$ = new Subject();
	public feedbackMode: string;
	public viewMode: string;

	public productTranslationParams: ProductTranslationParams;

	constructor (
		public sessionListDataSource: SessionListDataSource,
		private ngxSessionService: NgxSessionService,
		private eventService: EventService,
		private injector: Injector,
		private titleService: Title,
		private translateService: TranslateService,
		@Inject(selectedServiceToken) public selectedService: SelectedService,
		@Inject(userServiceToken) public userService: UserService,
		public routerService: RouterService,
		@Inject(sessionToken) private sessionModel: ReturnType<typeof SessionModel>,
		@Inject(activityToken) private activityModel: ActivityFactory,
		private transcriptCacheService: TranscriptCacheService,
		private activeBrandService: ActiveBrandService
	) {}

	public ngOnInit () {
		this.feedbackMode = this.routerService.getCurrentQueryParams().feedbackMode;
		this.viewMode = this.routerService.getCurrentQueryParams().viewMode;
		this.productTranslationParams = this.activeBrandService.getTranslationParams();
		this.selectedService.selectedSubject.asObservable()
			.pipe(
				map((selected: any) => {
					const session = selected.session;
					if (session == null) {
						return null;
					}

					return session.description != null && session.description !== ''
						? session.description
						: this.translateService.instant('breadcrumbs-untitled-session');
				}),
				distinctUntilChanged(),
				takeUntil(this.componentDestroyed$$)
			)
			.subscribe((sessionTitle) => this.updatePageTitle(sessionTitle));
		this.setSessionAndLicense();

		const eventNames = [
			EVENT_NAMES.FEEDBACK_SESSION_EXITED,
			EVENT_NAMES.SPEED_GRADER_CHANGE
		];
		this.eventSubscription = this.eventService.events
			.pipe(filter((ev: GoEvent) => Object.values<string>(eventNames).includes(ev.name)))
			.subscribe((ev: GoEvent) => {
				switch (ev.name) {
					case EVENT_NAMES.FEEDBACK_SESSION_EXITED:
						this.navigateToPreviousRoute();
						break;
					case EVENT_NAMES.SPEED_GRADER_CHANGE:
						// component is not destroyed on change now in lti, so we need this to make sure
						// we have the right session and transcript when we navigate to the new session
						this.isFullyLoaded$$.next(false);
						this.transcriptCacheService.clearCache();

						this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_SESSION_VIEW, {
							session_id: ev.data.newSessionId
						}).then(() => {
							this.setSessionAndLicense();
						});
						break;
				}
			});
	}

	public sessionReady (selectedSession) {
		if (!selectedSession.isAwaitingStart()) {
			this.sessionListDataSource.connect()
				.pipe(take(1)).subscribe((data) => {
					const sessionIds = data.map((s) => s.session_id);
					if (!sessionIds.includes(this.selectedService.getSession().session_id)) {
						this.sessionListDataSource.addSession(this.selectedService.getSession());
					}

					this.options$$.next({
						speedGraderList: sessionIds
					});
					this.otherSessions = data;

					// trigger ng1 digest cycle
					this.injector.get('$rootScope').$new().$evalAsync();
				});
		}

		// save is_fully_loaded to session so we dont have to load it again
		this.sessionListDataSource.editSession(this.selectedService.getSession());
	}

	public ngOnDestroy (): void {
		this.transcriptCacheService.clearCache();
		this.eventSubscription?.unsubscribe();
		this.componentDestroyed$$.next(true);
		this.componentDestroyed$$.complete();
	}

	private updatePageTitle (sessionTitle: string | null): void {
		if (sessionTitle == null) {
			return;
		}
		this.titleService.setTitle(
			this.translateService.instant('document-title_page', { name: sessionTitle, ...this.productTranslationParams })
		);
	}

	private navigateToPreviousRoute (): void {
		const previousRoute = sessionStorage.getItem('previousActivityRoute');

		if (previousRoute) {
			const routeData = JSON.parse(previousRoute);
			if (routeData.routeName && routeData.params) {
				this.routerService.navigateTo(routeData.routeName, routeData.params);
				return;
			}
		}
		this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_ACTIVITY_VIEW, {
			activity_id: this.selectedService.getActivity().activity_id
		});
	}

	/**
	 * Set session and license in selected service if not available
	 * Complete loading after all info retrieved
	 * @private
	 */
	private setSessionAndLicense () {
		const selectedSession = this.selectedService.getSession();

		let sessionObservable;
		if (selectedSession &&
			selectedSession.is_fully_loaded &&
			parseInt(selectedSession.session_id, 10) === parseInt(this.routerService.getCurrentParams().session_id, 10)
		) {
			this.selectedService.setSession(selectedSession);
			sessionObservable = of(selectedSession);
			this.sessionReady(selectedSession);
		} else {
			sessionObservable = this.ngxSessionService.getSession(
				Number(this.routerService.getCurrentParams().session_id),
				[
					'activity',
					'activity.attachments',
					'activity.recording_instructions',
					'activity.feedback_instructions',
					'player_time_sync_events',
					'activity.source_media',
					'activity.llmDefinitions'
				]
			).pipe(
				tap((session)=> {
					session = this.sessionModel.model(session);
					this.selectedService.setSession(session);
					this.sessionReady(session);
					session.is_fully_loaded = true;
					if (!this.selectedService.getActivity()) {
						this.selectedService.setActivity(this.activityModel.model(session.activity));
					}
				})
			);
		}

		const licenseObservable = this.selectedService.selectedSubject
			.asObservable()
			.pipe(
				takeUntil(this.componentDestroyed$$),
				filter((selected: any) => selected.group)
			);

		combineLatest([sessionObservable, licenseObservable])
			.pipe(
				takeUntil(this.componentDestroyed$$)
			)
			.subscribe(() => {
				this.isFullyLoaded$$.next(true);
			});
	}
}
